import React, { useState, useEffect } from "react";
import { useNotification } from "../../context/NotificationContext";
import campusService from "../../services/campusService";
import universityService from "../../services/universityService";
import AppTable from "../../components/ui/AppTable";
import ListToolbar from "../../components/ui/ListToolbar";

const CampusList = ({ onEdit, onView, onDelete, refreshTrigger }) => {
  const [campuses, setCampuses] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalPages: 0,
    totalElements: 0,
    pageSize: 10,
    isLastPage: false,
  });

  // Filter states
  const [filters, setFilters] = useState({
    search: "",
    universityId: "",
    city: "",
    country: "",
  });
  const [searchTimeout, setSearchTimeout] = useState(null);

  const { showSuccess, showError } = useNotification();

  useEffect(() => {
    fetchUniversities();
    fetchCampuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.pageSize, refreshTrigger]);

  useEffect(() => {
    // Debounced search and filter
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      fetchCampuses(0); // Reset to first page when filtering
    }, 500);

    setSearchTimeout(timeout);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.universityId, filters.city, filters.country]);

  const fetchUniversities = async () => {
    try {
      const response = await universityService.getUniversities(0, 100);
      if (response.success) {
        setUniversities(response.data.data);
      }
    } catch (error) {
      showError("Failed to fetch universities");
      console.error("Failed to fetch universities:", error);
    }
  };

  const fetchCampuses = async (page = pagination.currentPage) => {
    try {
      setLoading(true);
      const response = await campusService.getCampuses(
        page,
        pagination.pageSize,
        filters.search,
        filters.universityId || null,
        filters.city,
        filters.country
      );

      if (response.success) {
        setCampuses(response.data.data);
        setPagination({
          currentPage: response.data.currentPage,
          totalPages: response.data.totalPages,
          totalElements: response.data.totalElements,
          pageSize: response.data.pageSize,
          isLastPage: response.data.isLastPage,
        });
      }
    } catch (error) {
      console.error("Failed to fetch campuses:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  };

  const handlePageSizeChange = (newSize) => {
    setPagination((prev) => ({
      ...prev,
      pageSize: parseInt(newSize),
      currentPage: 0,
    }));
  };

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      universityId: "",
      city: "",
      country: "",
    });
  };

  const handleDelete = async (campus) => {
    if (
      window.confirm(
        `Are you sure you want to delete "${campus.nameEn}" campus?`
      )
    ) {
      try {
        const response = await campusService.deleteCampus(campus.id);
        if (response.success) {
          showSuccess("Campus deleted successfully");
          fetchCampuses();
        }
      } catch (error) {
        console.error("Failed to delete campus:", error);
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getActiveFiltersCount = () => {
    return Object.values(filters).filter(
      (value) => value && value.toString().trim()
    ).length;
  };

  const formatLocation = (campus) => {
    if (!campus.city && !campus.country) return "Not specified";
    return [campus.city, campus.country].filter(Boolean).join(", ");
  };

  return (
    <div className="campus-list">
      <ListToolbar
        title="Campuses"
        subtitle={`${pagination.totalElements} campus${pagination.totalElements !== 1 ? "es" : ""} found`}
      >
        <div className="row g-3">
          <div className="col-md-3">
            <label className="form-label small fw-medium">Search</label>
            <div className="input-group">
              <span className="input-group-text bg-light border-end-0">
                <i className="fas fa-search text-muted"></i>
              </span>
              <input
                type="text"
                className="form-control border-start-0"
                placeholder="Search campuses..."
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
              />
            </div>
          </div>

          <div className="col-md-3">
            <label className="form-label small fw-medium">University</label>
            <select
              className="form-select"
              value={filters.universityId}
              onChange={(e) => handleFilterChange("universityId", e.target.value)}
            >
              <option value="">All Universities</option>
              {universities.map((university) => (
                <option key={university.id} value={university.id}>
                  {university.nameEn}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label small fw-medium">City</label>
            <input
              type="text"
              className="form-control"
              placeholder="Filter by city..."
              value={filters.city}
              onChange={(e) => handleFilterChange("city", e.target.value)}
            />
          </div>

          <div className="col-md-3">
            <label className="form-label small fw-medium">Country</label>
            <input
              type="text"
              className="form-control"
              placeholder="Filter by country..."
              value={filters.country}
              onChange={(e) => handleFilterChange("country", e.target.value)}
            />
          </div>

          <div className="col-md-3 d-flex align-items-end">
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">Show:</span>
              <select
                className="form-select form-select-sm"
                style={{ width: "auto" }}
                value={pagination.pageSize}
                onChange={(e) => handlePageSizeChange(e.target.value)}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span className="text-muted small">entries</span>
            </div>
          </div>

          {getActiveFiltersCount() > 0 && (
            <div className="col-auto d-flex align-items-end">
              <button className="btn btn-sm btn-outline-secondary" onClick={clearFilters}>
                Clear All
              </button>
            </div>
          )}
        </div>
      </ListToolbar>

      {/* Campuses Table */}
      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mt-2">Loading campuses...</p>
            </div>
          ) : campuses.length === 0 ? (
            <div className="text-center py-5">
              <i
                className="fas fa-building text-muted mb-3"
                style={{ fontSize: "3rem" }}
              ></i>
              <h5 className="text-muted">No Campuses Found</h5>
              <p className="text-muted">
                {getActiveFiltersCount() > 0
                  ? "No campuses match your current filters"
                  : "No campuses available"}
              </p>
              {getActiveFiltersCount() > 0 && (
                <button
                  className="btn btn-outline-primary"
                  onClick={clearFilters}
                >
                  Clear Filters
                </button>
              )}
            </div>
          ) : (
            <AppTable responsive>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Campus</th>
                  <th>University</th>
                  <th>Location</th>
                  <th>Created</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {campuses.map((campus, index) => (
                  <tr key={campus.id}>
                    <td className="align-middle">
                      {pagination.currentPage * pagination.pageSize +
                        index +
                        1}
                    </td>
                    <td className="align-middle">
                      <div className="d-flex align-items-center">
                        <div
                          className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-3"
                          style={{ width: "40px", height: "40px" }}
                        >
                          <i className="fas fa-building text-white"></i>
                        </div>
                        <div>
                          <div className="fw-bold text-dark">
                            {campus.nameEn}
                          </div>
                          <small className="text-muted">
                            {campus.nameLocal}
                          </small>
                        </div>
                      </div>
                    </td>
                    <td className="align-middle">
                      <div className="fw-medium text-primary">
                        {campus.universityName}
                      </div>
                      <small className="text-muted">
                        University ID: {campus.universityId}
                      </small>
                    </td>
                    <td className="align-middle">
                      {campus.city || campus.country ? (
                        <span className="text-muted">
                          <i className="fas fa-map-marker-alt me-1 text-muted"></i>
                          {formatLocation(campus)}
                        </span>
                      ) : (
                        <span className="text-muted">
                          <i className="fas fa-question-circle me-1"></i>
                          Not specified
                        </span>
                      )}
                    </td>
                    <td className="align-middle">
                      <small className="text-muted">
                        {formatDate(campus.createdAt)}
                      </small>
                    </td>
                    <td className="align-middle text-center">
                      <div className="btn-group" role="group">
                        <button
                          className="btn btn-sm btn-outline-info"
                          onClick={() => onView(campus)}
                          title="View Details"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => onEdit(campus)}
                          title="Edit Campus"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(campus)}
                          title="Delete Campus"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </AppTable>
          )}
        </div>

        {/* Pagination */}
        {!loading && campuses.length > 0 && (
          <div className="card-footer bg-light border-0">
            <div className="row align-items-center">
              <div className="col-md-6">
                <span className="text-muted small">
                  Showing {pagination.currentPage * pagination.pageSize + 1} to{" "}
                  {Math.min(
                    (pagination.currentPage + 1) * pagination.pageSize,
                    pagination.totalElements
                  )}{" "}
                  of {pagination.totalElements} entries
                </span>
              </div>
              <div className="col-md-6">
                <nav>
                  <ul className="pagination pagination-sm justify-content-end mb-0">
                    <li
                      className={`page-item ${
                        pagination.currentPage === 0 ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(pagination.currentPage - 1)}
                        disabled={pagination.currentPage === 0}
                      >
                        Previous
                      </button>
                    </li>
                    {Array.from(
                      { length: Math.min(5, pagination.totalPages) },
                      (_, i) => {
                        let pageNum;
                        if (pagination.totalPages <= 5) {
                          pageNum = i;
                        } else if (pagination.currentPage < 3) {
                          pageNum = i;
                        } else if (
                          pagination.currentPage >= pagination.totalPages - 2
                        ) {
                          pageNum = pagination.totalPages - 5 + i;
                        } else {
                          pageNum = pagination.currentPage - 2 + i;
                        }
                        return (
                          <li
                            key={pageNum}
                            className={`page-item ${
                              pagination.currentPage === pageNum ? "active" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(pageNum)}
                            >
                              {pageNum + 1}
                            </button>
                          </li>
                        );
                      }
                    )}
                    <li
                      className={`page-item ${
                        pagination.isLastPage ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(pagination.currentPage + 1)}
                        disabled={pagination.isLastPage}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampusList;
